import { useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";

import { Button } from "@/components/ui/Button";
import { PageTitleWithHelp } from "@/components/ui/title-help";
import { WirelessHubTabs } from "./wirelessUiShared";
import { WirelessReportDataPanel } from "./WirelessReportDataPanel";

const TABS = [
  { key: "checkins", label: "All checkins" },
  { key: "currents", label: "Hourly currents" },
  { key: "events", label: "All events" },
] as const;

type ReportTab = (typeof TABS)[number]["key"];

export function WirelessReportsPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const downloadCsvRef = useRef<(() => Promise<void>) | null>(null);
  const [downloadDisabled, setDownloadDisabled] = useState(true);
  const activeTab = useMemo((): ReportTab => {
    const raw = searchParams.get("tab");
    if (raw === "events" || raw === "currents") return raw;
    return "checkins";
  }, [searchParams]);

  function setTab(tab: string) {
    const next = new URLSearchParams(searchParams);
    next.set("tab", tab);
    setSearchParams(next, { replace: true });
  }

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <PageTitleWithHelp
          hoverHint="Portal-wide check-ins, hourly current packets (24 one-hour averages plus peak per upload), and fault events for a date range. Filter by device when needed. CSV export includes every matching row, not only the current page."
        >
          Reports
        </PageTitleWithHelp>
        <Button
          type="button"
          disabled={downloadDisabled}
          onClick={() => {
            void downloadCsvRef.current?.();
          }}
        >
          Download CSV
        </Button>
      </div>

      <WirelessHubTabs ariaLabel="Wireless reports" tabs={[...TABS]} activeKey={activeTab} onChange={setTab} />

      {activeTab === "checkins" ? (
        <WirelessReportDataPanel
          kind="checkins"
          downloadCsvRef={downloadCsvRef}
          onDownloadDisabledChange={setDownloadDisabled}
        />
      ) : null}
      {activeTab === "currents" ? (
        <WirelessReportDataPanel
          kind="currents"
          downloadCsvRef={downloadCsvRef}
          onDownloadDisabledChange={setDownloadDisabled}
        />
      ) : null}
      {activeTab === "events" ? (
        <WirelessReportDataPanel
          kind="events"
          downloadCsvRef={downloadCsvRef}
          onDownloadDisabledChange={setDownloadDisabled}
        />
      ) : null}
    </div>
  );
}
